/**
 * Folded tent calendars: the panels of the flat (unfolded) sheet.
 *
 * - classic: A4-style tent card, top to bottom: face B (Jul–Dec, upside
 *   down) | face A (Jan–Jun) | base (unprinted) | glue tab (unprinted).
 * - dl-landscape: DL tent, panels 210 × 99 stacked top to bottom:
 *   face B (Jul–Dec, upside down) | face A (Jan–Jun) | base with other
 *   information (upright) | 15 mm tape strip (blank). Flat 210 × 312.
 * - dl-portrait-6 / dl-portrait-4: DL tent standing as a three-sided tower,
 *   panels 99 × 210 side by side, all upright, then a 15 mm tape strip on the
 *   right. 6-month: Jan–Jun | Jul–Dec | side panel with other information.
 *   4-month: Jan–Apr | May–Aug | Sep–Dec. Flat 312 × 210.
 *
 * DL tents: bleed on every outer edge except the tape strip's edge (the
 * last panel's bleed runs 3 mm under the tape), no marks of any kind.
 */
import type { Product } from './schema.js';

export type TentLayoutKind = 'classic' | 'dl-landscape' | 'dl-portrait-6' | 'dl-portrait-4';
export type TentPanelKey = 'faceA' | 'faceB' | 'info' | 'tri1' | 'tri2' | 'tri3' | 'base' | 'tab';

export interface TentPanel {
  key: TentPanelKey;
  /** panel rectangle on the flat trimmed sheet, mm, top-left origin */
  x: number;
  y: number;
  w: number;
  h: number;
  /** printed upside down so it reads upright once folded */
  rotate: 0 | 180;
  /** which panel edges may carry bleed (outer sheet edges), in the panel's own upright frame */
  bleed: { top: boolean; right: boolean; bottom: boolean; left: boolean };
  /** base of the classic card and the glue tab / tape strip stay blank */
  printed: boolean;
}

export interface TentFlat {
  /** flat trimmed sheet */
  w: number;
  h: number;
  panels: TentPanel[];
  /** fold / crease lines on the trimmed sheet (guides only — never printed on production files) */
  folds: { x1: number; y1: number; x2: number; y2: number }[];
  /** which edges of the flat sheet carry bleed (the tape strip's edge does not) */
  edges: { top: boolean; right: boolean; bottom: boolean; left: boolean };
}

export const TENT_LAYOUT_LABELS: Record<TentLayoutKind, string> = {
  classic: 'Tent card: two faces, base and glue tab',
  'dl-landscape': 'DL landscape tent: Jan–Jun / Jul–Dec + base information',
  'dl-portrait-6': 'DL portrait tower: Jan–Jun | Jul–Dec | side information',
  'dl-portrait-4': 'DL portrait tower: Jan–Apr | May–Aug | Sep–Dec',
};

/** Months on each calendar panel. */
export const TENT_PANEL_MONTHS: Partial<Record<TentPanelKey, number[]>> = {
  faceA: [1, 2, 3, 4, 5, 6],
  faceB: [7, 8, 9, 10, 11, 12],
  tri1: [1, 2, 3, 4],
  tri2: [5, 6, 7, 8],
  tri3: [9, 10, 11, 12],
};

/** DL tent sizes: three 99 mm panels and a 15 mm tape strip. */
export const DL_PANEL = 99;
export const DL_TAPE = 15;

type TentProduct = Pick<Product, 'widthMm' | 'heightMm' | 'tent'>;
const ALL = { top: true, right: true, bottom: true, left: true };

export function tentFlat(p: TentProduct): TentFlat {
  const W = p.widthMm, H = p.heightMm;
  const { baseMm, tabMm } = p.tent;
  const layout = p.tent.layout ?? 'classic';
  if (layout === 'dl-landscape') {
    const ph = (H - tabMm) / 3;
    const side = { left: true, right: true };
    return {
      w: W, h: H,
      panels: [
        { key: 'faceB', x: 0, y: 0, w: W, h: ph, rotate: 180, bleed: { ...side, top: false, bottom: true }, printed: true },
        { key: 'faceA', x: 0, y: ph, w: W, h: ph, rotate: 0, bleed: { ...side, top: false, bottom: false }, printed: true },
        { key: 'info', x: 0, y: 2 * ph, w: W, h: ph, rotate: 0, bleed: { ...side, top: false, bottom: true }, printed: true },
        { key: 'tab', x: 0, y: 3 * ph, w: W, h: tabMm, rotate: 0, bleed: { ...side, top: false, bottom: false }, printed: false },
      ],
      folds: [ph, 2 * ph, 3 * ph].map((y) => ({ x1: 0, y1: y, x2: W, y2: y })),
      edges: { top: true, left: true, right: true, bottom: false },
    };
  }
  if (layout === 'dl-portrait-6' || layout === 'dl-portrait-4') {
    const pw = (W - tabMm) / 3;
    const keys: TentPanelKey[] = layout === 'dl-portrait-6' ? ['faceA', 'faceB', 'info'] : ['tri1', 'tri2', 'tri3'];
    return {
      w: W, h: H,
      panels: [
        ...keys.map((key, i): TentPanel => ({ key, x: i * pw, y: 0, w: pw, h: H, rotate: 0, bleed: { top: true, bottom: true, left: i === 0, right: i === 2 }, printed: true })),
        { key: 'tab', x: 3 * pw, y: 0, w: tabMm, h: H, rotate: 0, bleed: { top: true, bottom: true, left: false, right: false }, printed: false },
      ],
      folds: [pw, 2 * pw, 3 * pw].map((x) => ({ x1: x, y1: 0, x2: x, y2: H })),
      edges: { top: true, bottom: true, left: true, right: false },
    };
  }
  // classic
  const fh = Math.max(10, (H - baseMm - tabMm) / 2);
  const panels = ([
    { key: 'faceB', x: 0, y: 0, w: W, h: fh, rotate: 180, bleed: { top: false, right: true, bottom: true, left: true }, printed: true },
    { key: 'faceA', x: 0, y: fh, w: W, h: fh, rotate: 0, bleed: { top: false, right: true, bottom: baseMm <= 0 && tabMm <= 0, left: true }, printed: true },
    { key: 'base', x: 0, y: 2 * fh, w: W, h: baseMm, rotate: 0, bleed: { top: false, right: true, bottom: false, left: true }, printed: false },
    { key: 'tab', x: 0, y: 2 * fh + baseMm, w: W, h: tabMm, rotate: 0, bleed: { top: false, right: true, bottom: true, left: true }, printed: false },
  ] as TentPanel[]).filter((x) => x.h > 0);
  // the apex, then the fold to the base / tab (none when the two faces make the whole sheet)
  const ys = [fh, ...(baseMm > 0 || tabMm > 0 ? [2 * fh] : []), ...(baseMm > 0 && tabMm > 0 ? [2 * fh + baseMm] : [])];
  return { w: W, h: H, panels, folds: ys.map((y) => ({ x1: 0, y1: y, x2: W, y2: y })), edges: ALL };
}

/** Printed panels in page order (the editor's pages). */
export function tentPagePanels(p: TentProduct): TentPanel[] {
  const order: TentPanelKey[] = ['faceA', 'faceB', 'tri1', 'tri2', 'tri3', 'info'];
  return tentFlat(p).panels.filter((x) => x.printed).sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

/** Size of one printed panel (page) of a tent. */
export function tentPanelSize(p: TentProduct, key: string): { w: number; h: number } | null {
  const panel = tentFlat(p).panels.find((x) => x.key === key && x.printed);
  return panel ? { w: panel.w, h: panel.h } : null;
}

/**
 * Size of the flat piece including bleed on the edges that carry it
 * (DL landscape 210 × 312 → 216 × 315). This is what is imposed on SRA3.
 */
export function tentPieceSize(p: TentProduct & Pick<Product, 'bleedMm'>): { w: number; h: number; left: number; top: number } {
  const f = tentFlat(p);
  const b = p.bleedMm;
  const left = f.edges.left ? b : 0, right = f.edges.right ? b : 0, top = f.edges.top ? b : 0, bottom = f.edges.bottom ? b : 0;
  return { w: f.w + left + right, h: f.h + top + bottom, left, top };
}

/** Product settings for a DL tent layout. */
export function dlTentProduct(layout: Exclude<TentLayoutKind, 'classic'>): Partial<Product> {
  const landscape = layout === 'dl-landscape';
  const long = 3 * DL_PANEL + DL_TAPE; // 312
  return {
    productType: 'tent', sheetPreset: 'custom', orientation: landscape ? 'portrait' : 'landscape',
    widthMm: landscape ? 210 : long, heightMm: landscape ? long : 210,
    tent: { baseMm: DL_PANEL, tabMm: DL_TAPE, layout },
    bleedMm: 3, safeMarginMm: 3, cropMarks: false,
    bindingEdge: 'none', bindingClearanceMm: 0, includeCover: false, duplex: false, calendarArea: 'full',
    finishing: landscape
      ? 'DL landscape tent: crease the 3 folds, double-sided tape on the 15 mm strip.'
      : 'DL portrait tower: crease the 3 folds, double-sided tape on the 15 mm strip.',
  };
}
